import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { taskMetadataApi } from '../../services/taskMetadata';
import {
  TaskCategory,
  TaskCategoryPayload,
  TaskDefinition,
  TaskDefinitionPayload,
  TaskSubtaskRelation,
  TaskSubtaskRelationPayload,
} from '../../types';

interface TaskMetadataState {
  taskDefinitions: TaskDefinition[];
  taskCategories: TaskCategory[];
  taskSubtaskRelations: TaskSubtaskRelation[];
  loading: boolean;
  saving: boolean;
  refreshing: boolean;
  error: string | null;
}

const initialState: TaskMetadataState = {
  taskDefinitions: [],
  taskCategories: [],
  taskSubtaskRelations: [],
  loading: false,
  saving: false,
  refreshing: false,
  error: null,
};

const upsertByIdOrMatch = <T extends { id?: number }>(
  list: T[],
  item: T,
  matcher: (current: T) => boolean
) => {
  const index = list.findIndex((current) =>
    item.id ? current.id === item.id : matcher(current)
  );

  if (index >= 0) {
    list[index] = item;
    return;
  }

  list.unshift(item);
};

export const fetchTaskMetadata = createAsyncThunk(
  'taskMetadata/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const [definitionsRes, categoriesRes, relationsRes] = await Promise.all([
        taskMetadataApi.getTaskDefinitions(),
        taskMetadataApi.getTaskCategories(),
        taskMetadataApi.getTaskSubtaskRelations(),
      ]);

      return {
        taskDefinitions: definitionsRes.data,
        taskCategories: categoriesRes.data,
        taskSubtaskRelations: relationsRes.data,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || '获取任务元数据失败');
    }
  }
);

export const saveTaskDefinition = createAsyncThunk(
  'taskMetadata/saveTaskDefinition',
  async (payload: TaskDefinitionPayload, { rejectWithValue }) => {
    try {
      const response = await taskMetadataApi.saveTaskDefinition(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '保存任务定义失败');
    }
  }
);

export const saveTaskCategory = createAsyncThunk(
  'taskMetadata/saveTaskCategory',
  async (payload: TaskCategoryPayload, { rejectWithValue }) => {
    try {
      const response = await taskMetadataApi.saveTaskCategory(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '保存任务分类失败');
    }
  }
);

export const saveTaskSubtaskRelation = createAsyncThunk(
  'taskMetadata/saveTaskSubtaskRelation',
  async (payload: TaskSubtaskRelationPayload, { rejectWithValue }) => {
    try {
      const response = await taskMetadataApi.saveTaskSubtaskRelation(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '保存子任务关系失败');
    }
  }
);

export const refreshTaskMetadataRegistry = createAsyncThunk(
  'taskMetadata/refreshRegistry',
  async (_, { rejectWithValue }) => {
    try {
      const response = await taskMetadataApi.refreshTaskMetadataRegistry();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '刷新任务元数据注册中心失败');
    }
  }
);

const taskMetadataSlice = createSlice({
  name: 'taskMetadata',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskMetadata.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTaskMetadata.fulfilled,
        (
          state,
          action: PayloadAction<{
            taskDefinitions: TaskDefinition[];
            taskCategories: TaskCategory[];
            taskSubtaskRelations: TaskSubtaskRelation[];
          }>
        ) => {
          state.loading = false;
          state.taskDefinitions = action.payload.taskDefinitions;
          state.taskCategories = action.payload.taskCategories;
          state.taskSubtaskRelations = action.payload.taskSubtaskRelations;
        }
      )
      .addCase(fetchTaskMetadata.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || '获取任务元数据失败';
      })
      .addCase(saveTaskDefinition.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        saveTaskDefinition.fulfilled,
        (state, action: PayloadAction<TaskDefinition>) => {
          state.saving = false;
          upsertByIdOrMatch(
            state.taskDefinitions,
            action.payload,
            (item) =>
              item.taskType === action.payload.taskType ||
              item.taskKey === action.payload.taskKey
          );
        }
      )
      .addCase(saveTaskDefinition.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || '保存任务定义失败';
      })
      .addCase(saveTaskCategory.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        saveTaskCategory.fulfilled,
        (state, action: PayloadAction<TaskCategory>) => {
          state.saving = false;
          upsertByIdOrMatch(
            state.taskCategories,
            action.payload,
            (item) => item.categoryCode === action.payload.categoryCode
          );
        }
      )
      .addCase(saveTaskCategory.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || '保存任务分类失败';
      })
      .addCase(saveTaskSubtaskRelation.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        saveTaskSubtaskRelation.fulfilled,
        (state, action: PayloadAction<TaskSubtaskRelation>) => {
          state.saving = false;
          upsertByIdOrMatch(
            state.taskSubtaskRelations,
            action.payload,
            (item) => item.relationName === action.payload.relationName
          );
        }
      )
      .addCase(saveTaskSubtaskRelation.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || '保存子任务关系失败';
      })
      .addCase(refreshTaskMetadataRegistry.pending, (state) => {
        state.refreshing = true;
        state.error = null;
      })
      .addCase(refreshTaskMetadataRegistry.fulfilled, (state) => {
        state.refreshing = false;
      })
      .addCase(refreshTaskMetadataRegistry.rejected, (state, action) => {
        state.refreshing = false;
        state.error = (action.payload as string) || '刷新任务元数据注册中心失败';
      });
  },
});

export default taskMetadataSlice.reducer;
